import { EMAIL_VALIDATION_REGEX, WEEK_DAY_NAMES } from "./constants";
import isString from "lodash/isString";
import { addDays, differenceInDays, differenceInMonths, format, getDay, getMonth, isSameDay } from "date-fns";

export function isEmailValid(email) {
    return EMAIL_VALIDATION_REGEX.test(email);
}

export function getWeekDay(date) {
    return WEEK_DAY_NAMES[date.getDay()];
}

export function toDate(date) {
    if (isString(date)) {
        return new Date(date);
    }
    return date;
}

export function formatReadableDateAndTime({ dateTime, currentDateTime }) {
    return formatReadableDate({ dateTime, currentDateTime, includeTime: true });
}

export function formatReadableDateAndTimeShort({ dateTime, currentDateTime }) {
    return formatReadableDate({ dateTime, currentDateTime, includeTime: true, shortVersion: true });
}

export function formatReadableDateShort({ dateTime, currentDateTime }) {
    return formatReadableDate({
        dateTime,
        currentDateTime,
        includeTimeThisYear: false,
        shortVersion: true,
    });
}

export function formatReadableDate({ dateTime, currentDateTime = new Date(), shortVersion = false, includeTimeThisYear = false, includeTime = false, includeYear = false }) {
    if (!dateTime) {
        return dateTime;
    }
    dateTime = toDate(dateTime);
    const months = shortVersion ? "MMM" : "MMMM";

    const diffInMonths = Math.abs(differenceInMonths(dateTime, currentDateTime));
    if (diffInMonths < 12 && !(diffInMonths === 11 && getMonth(dateTime) === getMonth(currentDateTime))) {
        return format(dateTime, `d ${months}${includeYear ? " YYYY" : ""}${includeTimeThisYear || includeTime ? ", HH:mm" : ""}`);
    } else {
        return format(dateTime, `d ${months} yyyy${includeTime ? ", HH:mm" : ""}`);
    }
}

export function timeFromDate(date) {
    if (!date) {
        return date;
    }
    date = toDate(date);
    return format(date, "HH:mm");
}

export function formatDateTimeToDate(date) {
    if (!date) {
        return date;
    }
    date = toDate(date);
    return format(date, "yyyy-MM-dd");
}

export function formatDateToTimeDate(dateTime) {
    if (!dateTime) {
        return dateTime;
    }
    return toDate(dateTime).toISOString();
}

export function padWithZeros(id, len) {
    id += "";
    while (id.length < len) {
        id = "0" + id;
    }
    return id;
}

function isToday({ dateTime, currentDateTime = new Date() }) {
    return isSameDay(dateTime, currentDateTime);
}

function isYesterday({ dateTime, currentDateTime = new Date() }) {
    return isToday({ dateTime: addDays(dateTime, 1), currentDateTime });
}

export function humanReadableDateTimeVague({ dateTime, currentDateTime = new Date() }) {
    let date = toDate(dateTime);
    let diff = currentDateTime - date;
    let seconds = Math.round(diff / 1000);
    let minutes = Math.round(diff / 1000 / 60);
    let hours = Math.round(diff / 1000 / 60 / 60);
    let days = Math.round(diff / 1000 / 60 / 60 / 24);
    let weeks = Math.round(diff / 1000 / 60 / 60 / 24 / 7);

    if (seconds < 60) {
        return "Just now";
    } else if (minutes < 60) {
        return minutes + " " + (minutes === 1 ? "min" : "min") + " ago";
    } else if (hours < 24) {
        return hours + " " + (hours === 1 ? "hour" : "hours") + " ago";
    } else if (days <= 6) {
        return days + " " + (days === 1 ? "day" : "days") + " ago";
    } else if (weeks <= 3) {
        return weeks + " " + (weeks === 1 ? "week" : "weeks") + " ago";
    }
    return formatReadableDateShort({ dateTime, currentDateTime });
}

const weekdays = {
    0: "Sunday",
    1: "Monday",
    2: "Tuesday",
    3: "Wednesday",
    4: "Thursday",
    5: "Friday",
    6: "Saturday",
};

export function humanReadableDateTime({ dateTime, currentDateTime = new Date(), shortVersion = true, locale }) {
    dateTime = toDate(dateTime);
    let diff = currentDateTime - dateTime;
    let seconds = Math.round(diff / 1000);
    let minutes = Math.round(diff / 1000 / 60);

    if (seconds <= 60) {
        return "Just now";
    } else if (minutes < 60) {
        return minutes + " " + (minutes === 1 ? "minute" : "minutes") + " ago";
    } else {
        return humanReadableDate({ dateTime, currentDateTime, shortVersion, includeTime: true, locale });
    }
}

export function humanReadableDate({ dateTime, currentDateTime = new Date(), shortVersion = true, includeTime = false, locale }) {
    dateTime = toDate(dateTime);

    const time = timeFromDate(dateTime);
    const timeString = includeTime ? ` at ${time}` : "";

    if (isToday({ dateTime, currentDateTime })) {
        return `Today${timeString}`;
    } else if (isYesterday({ dateTime, currentDateTime })) {
        return `Yesterday${timeString}`;
    } else if (Math.abs(differenceInDays(dateTime, currentDateTime)) <= 6) {
        return `${weekdays[getDay(dateTime)]}${timeString}`;
    }

    return formatReadableDate({
        dateTime,
        currentDateTime,
        shortVersion,
        includeTimeThisYear: includeTime,
        locale,
    });
}

export function isUrlSameAsCurrentPathname(url) {
    const toPathname = url?.split("?")[0];
    const pathname = window.location.pathname;
    return toPathname === pathname;
}

export function getSelectableFontsForBrand({ fonts, brandName }) {
    return (
        fonts?.filter((font) => {
            const approvedBrands = font?.approvedBrands;
            const approvedBrandsPartialMatch = font?.approvedBrandsPartialMatch;
            if (!approvedBrands && !approvedBrandsPartialMatch) {
                return true;
            }
            if (
                approvedBrands?.includes(brandName) ||
                approvedBrandsPartialMatch?.find((partialMatch) => {
                    return brandName?.includes(partialMatch);
                })
            ) {
                return true;
            }
            return false;
        }) || []
    );
}

export function stringHasAtLeastXSlashes(text, { nrOfSlashes = 2 } = {}) {
    return (text?.match(/\//g) || []).length >= nrOfSlashes;
}

export function formatReadableStatus(input) {
    return input
        ?.toLowerCase() // Convert to lowercase
        .replace(/_/g, " ") // Replace underscores with spaces
        .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize the first letter of each word
}

export function getNameAbbreviation({ name }) {
    const splitted = name?.split(" ");
    let result = "";
    if (splitted?.length > 1) {
        result = splitted?.[0]?.[0] + splitted?.[splitted?.length - 1]?.[0];
    } else {
        result = splitted?.[0]?.[0] + (splitted?.[0]?.[1] || splitted?.[0]?.[0]);
    }
    return (result || "").toUpperCase();
}

// Phones & Tablets stuff

export function isIPad({ userAgent }) {
    if (/iPad/.test(userAgent)) {
        // For the adventurous && !window.MSStream) {
        return true;
    }
    return false;

    // TODO I simply removed this
    // Newer iPad mimics Mac Safari, so checking for iPad is not enough.
    // return windowObject.navigator.maxTouchPoints && windowObject.navigator.maxTouchPoints > 2 && /MacIntel/.test(windowObject.navigator.platform);
}

export function isIPhone({ userAgent, windowObject = {} } = {}) {
    return /iPhone/.test(userAgent) && !windowObject.MSStream;
}

export function isIPod({ userAgent, windowObject = {} } = {}) {
    return /iPod/.test(userAgent) && !windowObject.MSStream;
}

export function isIOS(params) {
    return isIPad(params) || isIPhone(params) || isIPod(params);
}

export function isWindowsPhone({ userAgent } = {}) {
    return /windows phone/i.test(userAgent);
}

export function isAndroid({ userAgent } = {}) {
    // Windows phones can have android in the agent so we must first check that it is not a windows phone
    return !isWindowsPhone({ userAgent }) && /android/i.test(userAgent);
}

export function isPhoneOrTablet(params) {
    return isIOS(params) || isWindowsPhone(params) || isAndroid(params);
}

export function isPhone(params = { windowObject: window, userAgent: window.navigator.userAgent }) {
    return isPhoneOrTablet(params) && !isIPad(params);
}
